'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTheme } from '@/app/[locale]/ThemeProvider';
import MobileNavLink from './MobileNavLink';

interface MobileMenuButtonsProps {
  openLabel: string;
  closeLabel: string;
  children: React.ReactNode;
  closeMenu?: () => void;
}

export default function MobileMenuButtons({ 
  openLabel, 
  closeLabel,
  children,
  closeMenu
}: MobileMenuButtonsProps) {
  const [menuopen, setMenuopen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { themePreference } = useTheme();

  const toggleMenu = () => setMenuopen(!menuopen);
  const closemenu = () => {
    setMenuopen(false);
    if (closeMenu) {
      closeMenu();
    }
  };

  // Deep clone children: only inject closemenu into MobileNavLink and LocaleCurrencyChanger components
  const cloneWithClosemenu = (node: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement(node)) return node;
    const element = node as React.ReactElement<{ children?: React.ReactNode, type?: { name: string } }>;
    const nodeChildren = element.props.children;
    const newProps: Partial<{ closemenu: () => void }> = {};

    // Only add closemenu to MobileNavLink and LocaleCurrencyChanger
    if (element.type === MobileNavLink || (element.type === 'LocaleCurrencyChanger')) {
      newProps.closemenu = closemenu;
    }

    // Recursively clone children
    const clonedChildren = nodeChildren
      ? React.Children.map(nodeChildren, cloneWithClosemenu)
      : nodeChildren;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return React.cloneElement(element, newProps as any, clonedChildren);
  };
  const childrenWithCloseMenu = React.Children.map(children, cloneWithClosemenu);

  // Automatically close menu on route change
  useEffect(() => {
    if (menuopen) {
      setMenuopen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  // Automatically close menu on theme change
  useEffect(() => {
    if (menuopen) {
      setMenuopen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themePreference]);

  return (
    <>
      <button 
        onClick={toggleMenu} 
        className="p-2 mr-2" 
        aria-label={menuopen ? closeLabel : openLabel}
      >
        {menuopen ? (
          <svg
            className="w-6 h-6 text-text-main/80"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            className="w-6 h-6 text-text-main/80"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        )}
      </button>
      
      {menuopen &&
        createPortal(
          <div 
            className="fixed inset-0 z-[120] flex flex-col bg-bg-main/10 backdrop-blur-sm border border-text-main/10" 
            onClick={closemenu}
          >
            <div 
              className="relative p-4 pt-12 bg-bg-main/90 backdrop-blur-lg border border-text-main/10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closemenu}
                className="absolute top-4 right-4"
                aria-label={closeLabel}
              >
                <svg
                  className="w-6 h-6 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              {childrenWithCloseMenu}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}